import React from 'react';
import { useDispatch } from 'react-redux';

import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';

import { Form, SimpleTextField, useSettings, useUserData, Utils } from 'app/main/components';

interface ResetPasswordDialogProps {
	showCancelButton?: boolean;
	onCancelButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const showResetPasswordDialog = (props?: ResetPasswordDialogProps) =>
	openDialog({
		children: <ResetPasswordDialog {...props} />,
		fullWidth: true,
		maxWidth: 'sm',
		onClose: () => {}
	});

export default function ResetPasswordDialog(props?: ResetPasswordDialogProps) {
	const dispatch = useDispatch();
	const userData = useUserData();
	const { t } = useSettings();

	const [passwordData, setPasswordData] = React.useState<{ oldPassword?: string; newPassword?: string }>({});

	const handlePasswordChange = () => {
		Utils.executeStored({
			action: 'ext_st_users_update_pass',
			params: {
				user_uid: userData.user_uid,
				user_pass: passwordData.oldPassword,
				user_new_pass: passwordData.newPassword
			},
			dispatch,
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				dispatch(closeDialog());
			},
			errorHandler: error => dispatch(Utils.alertMsg(t(error.data.message), 'error'))
		});
	};

	const sendChange = (name: string, value: string | null) => {
		setPasswordData(state => ({ ...state, [name]: value }));
	};

	return (
		<>
			<DialogTitle>{t('DIALOG_TITLE_RESET_PASSWORD')}</DialogTitle>
			<DialogContent>
				<Form id="resetPasswordDialog" onSubmit={handlePasswordChange}>
					<Grid container spacing={2}>
						<Grid size={12}>
							<SimpleTextField
								name="oldPassword"
								label={t('DIALOG_USER_PASSWORD_OLD')}
								type="password"
								autoComplete="current-password"
								sendChange={sendChange}
								isEdit
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={12}>
							<SimpleTextField
								name="newPassword"
								label={t('DIALOG_USER_PASSWORD_NEW')}
								type="password"
								autoComplete="new-password"
								sendChange={sendChange}
								isEdit
								required
								validations={{
									noNewPasswordMatchesOld: (values, value) =>
										values.oldPassword === value ? t('ERROR_NEW_PASSWORD_MATCH_OLD') : true
								}}
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={12}>
							<SimpleTextField
								name="confirmNewPassword"
								label={t('DIALOG_USER_PASSWORD_CONFIRM_NEW')}
								type="password"
								sendChange={sendChange}
								isEdit
								required
								validations={{ equalsField: 'newPassword' }}
								validationErrors={{
									isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
									equalsField: t('ERROR_PASSWORD_NO_MATCH')
								}}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				{props?.showCancelButton && (
					<Button
						onClick={event => {
							if (props.onCancelButtonClick) props.onCancelButtonClick(event);
							else dispatch(closeDialog());
						}}
					>
						{t('DIALOG_ACTION_BACK')}
					</Button>
				)}
				<Button type="submit" form="resetPasswordDialog">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
